import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Eye, Trash2, X, ChevronDown } from 'lucide-react';
import api from '../../services/api';

interface Deposit {
  id: number;
  vendor_id: number;
  vendor_name: string;
  deposit_date: string;
  amount: number;
  remaining_balance: number;
  currency: string;
  remarks: string;
  source_itinerary_id: number | null;
  status: string;
  created_by: string;
  created_at: string;
}

interface Usage {
  id: number;
  deposit_id: number;
  itinerary_id: number;
  amount_used: number;
  payment_reference: string;
  notes: string;
  used_by: string;
  used_at: string;
}

const FloatingDepositsDashboard: React.FC = () => {
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [usageHistory, setUsageHistory] = useState<Usage[]>([]);
  const [selectedDeposit, setSelectedDeposit] = useState<Deposit | null>(null);
  const [vendors, setVendors] = useState<{id: number, name: string}[]>([]);
  const [filters, setFilters] = useState({ status: 'ACTIVE', vendor_id: '' });
  const [vendorSearch, setVendorSearch] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const vendorDropdownRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState({
    vendor_id: '',
    vendor_name: '',
    deposit_date: new Date().toISOString().split('T')[0],
    amount: '',
    currency: 'IDR',
    remarks: '',
    source_itinerary_id: '',
  });

  const formatCurrency = (amount: number, currency: string) => {
    if (currency === 'INR') return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amount);
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);
  };

  const loadDeposits = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.vendor_id) params.append('vendor_id', filters.vendor_id);
      const response = await api.get(`/floating-deposits/?${params.toString()}`);
      if (response.data.success) setDeposits(response.data.data);
    } catch (error) {
      console.error('Error loading deposits:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadVendors = async () => {
    try {
      const response = await api.get('/vendors/');
      if (response.data.success) {
        setVendors(response.data.data.map((v: { id: number; name: string }) => ({ id: v.id, name: v.name })));
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...form,
        vendor_id: parseInt(form.vendor_id),
        amount: parseFloat(form.amount),
        source_itinerary_id: form.source_itinerary_id ? parseInt(form.source_itinerary_id) : null,
      };
      await api.post('/floating-deposits/', payload);
      setShowModal(false);
      resetForm();
      loadDeposits();
    } catch (error) {
      console.error('Error creating deposit:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this deposit?')) return;
    try {
      await api.delete(`/floating-deposits/${id}`);
      loadDeposits();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Cannot delete');
    }
  };

  const viewUsage = async (deposit: Deposit) => {
    try {
      setSelectedDeposit(deposit);
      const response = await api.get(`/floating-deposits/${deposit.id}/usage`);
      if (response.data.success) setUsageHistory(response.data.data);
      setShowUsageModal(true);
    } catch (error) {
      console.error('Error loading usage:', error);
    }
  };

  const resetForm = () => {
    setForm({ vendor_id: '', vendor_name: '', deposit_date: new Date().toISOString().split('T')[0], amount: '', currency: 'IDR', remarks: '', source_itinerary_id: '' });
    setVendorSearch('');
  };

  useEffect(() => { loadDeposits(); loadVendors(); }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(e.target as Node)) {
        setShowVendorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredVendors = vendors.filter(v =>
    v.name.toLowerCase().includes(vendorSearch.toLowerCase())
  );

  const handleVendorSelect = (v: { id: number; name: string }) => {
    setForm({ ...form, vendor_id: String(v.id), vendor_name: v.name });
    setVendorSearch(v.name);
    setShowVendorDropdown(false);
  };

  const totalActive = deposits.filter(d => d.status === 'ACTIVE').reduce((sum, d) => sum + d.remaining_balance, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Floating Deposits</h1>
            <p className="text-sm text-gray-500">Vendor overpayments available for future bookings</p>
          </div>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" /> Add Deposit
          </button>
        </div>

        {/* Summary */}
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-gray-500">Total Available Balance</p>
            <p className="text-xl font-bold text-green-700">{formatCurrency(totalActive, 'IDR')}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-gray-500">Active Deposits</p>
            <p className="text-xl font-bold text-blue-700">{deposits.filter(d => d.status === 'ACTIVE').length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-gray-500">Exhausted Deposits</p>
            <p className="text-xl font-bold text-gray-500">{deposits.filter(d => d.status === 'EXHAUSTED').length}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">All</option>
                <option value="ACTIVE">Active</option>
                <option value="EXHAUSTED">Exhausted</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Vendor</label>
              <input type="text" placeholder="Filter by vendor..." onChange={(e) => setFilters({ ...filters, vendor_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div className="flex items-end">
              <button onClick={loadDeposits} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center justify-center gap-2">
                <Search className="w-4 h-4" /> Search
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remaining</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remarks</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              ) : deposits.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No deposits found</td></tr>
              ) : deposits.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{d.vendor_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{new Date(d.deposit_date).toLocaleDateString('en-GB')}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{formatCurrency(d.amount, d.currency)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-green-700">{formatCurrency(d.remaining_balance, d.currency)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{d.source_itinerary_id ? `WH_${d.source_itinerary_id}` : '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{d.remarks || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${d.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {d.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => viewUsage(d)} className="text-blue-600 hover:text-blue-800" title="View Usage"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(d.id)} className="text-gray-600 hover:text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Deposit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Add Floating Deposit</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2" ref={vendorDropdownRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={vendorSearch}
                      onChange={(e) => {
                        setVendorSearch(e.target.value);
                        setForm({ ...form, vendor_id: '', vendor_name: '' });
                        setShowVendorDropdown(true);
                      }}
                      onFocus={() => setShowVendorDropdown(true)}
                      placeholder="Search vendor..."
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                    {showVendorDropdown && filteredVendors.length > 0 && (
                      <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredVendors.map(v => (
                          <li
                            key={v.id}
                            onMouseDown={() => handleVendorSelect(v)}
                            className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 cursor-pointer"
                          >
                            {v.name}
                          </li>
                        ))}
                      </ul>
                    )}
                    {showVendorDropdown && vendorSearch && filteredVendors.length === 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-400">
                        No vendors found
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input type="date" value={form.deposit_date} onChange={(e) => setForm({ ...form, deposit_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="IDR">IDR</option>
                    <option value="INR">INR</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source Itinerary (optional)</label>
                <input type="text" value={form.source_itinerary_id} onChange={(e) => setForm({ ...form, source_itinerary_id: e.target.value })} placeholder="e.g. 5213" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <input type="text" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="e.g. Overpaid on WH_5213" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={handleSubmit} disabled={!form.vendor_id || !form.amount} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Usage History Modal */}
      {showUsageModal && selectedDeposit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Usage History</h2>
                <p className="text-sm text-gray-500">{selectedDeposit.vendor_name} — {formatCurrency(selectedDeposit.amount, selectedDeposit.currency)}</p>
              </div>
              <button onClick={() => setShowUsageModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-4">
              {usageHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No usage yet</p>
              ) : (
                <div className="space-y-3">
                  {usageHistory.map((u) => (
                    <div key={u.id} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-900">WH_{u.itinerary_id}</span>
                        <span className="text-sm font-medium text-red-600">-{formatCurrency(u.amount_used, selectedDeposit.currency)}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(u.used_at).toLocaleDateString('en-GB')} {u.payment_reference && `• ${u.payment_reference}`}
                      </div>
                      {u.notes && <div className="text-xs text-gray-500 mt-1">{u.notes}</div>}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 pt-3 border-t flex justify-between">
                <span className="text-sm text-gray-600">Remaining Balance</span>
                <span className="text-sm font-bold text-green-700">{formatCurrency(selectedDeposit.remaining_balance, selectedDeposit.currency)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingDepositsDashboard;
